import React, { useMemo } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { motion, useScroll, useTransform } from 'framer-motion';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import Ticker from 'framer-motion-ticker';
import { PiBasketball, PiTrophy, PiToilet } from 'react-icons/pi';
import { FaFacebookF, FaInstagram, FaYoutube, FaXTwitter, FaTiktok } from 'react-icons/fa6';
import styles from './styles.module.css'

dayjs.extend(advancedFormat);

import {
    Box,
    Container,
    H1,
    H2,
    H3,
    H4,
    Type,
    P,
    Grid,
    Btn,
    BtnDot,
} from '@saladbob/sassafras'

import { useEnv } from '../providers/EnvProvider';
import Image from '../media/Image';
import Gallery from '../media/Gallery';
import PageTabs from '../navigation/PageTabs';
import Blocks from '../blocks/Blocks';

import Host from '../../types/Host';
import NoticeBar from '../../components/news/NoticeBar';
import type { BracketDepth } from '../../types/Bracket';

type Props = {
    host: Host,
    page?: string,
    children?: React.ReactNode,
    brackets?: boolean,
    bracket?: BracketDepth,
};

function useParallax(value, distance) {
    return useTransform(value, [0, 1], [0, distance]);
}

const WinnersHeading = ({ host, completedData }) => (
    <>
        <Box bgColor="tertiary">
            <Container size="lg" padding="xl">
                <H1 mb="0" weight="lighter">{host.activeTournament.title} {dayjs(host.activeTournament.start).format('YYYY')}</H1>
                <H2 font="jumbo" uppercase mb="md">The Final Score</H2>
                <P font="h2"><strong>{completedData.teams.length}</strong> teams laced up, played through <strong>{completedData.games.length}</strong> intense games across <strong>{completedData.courts.length}</strong> courts, leaving everything they had on the floor.</P>
                <P font="h2"><strong>{completedData.trophies.all}</strong> squads balled out, earning their spot among the elite as <strong>Gus Macker Trophy Earners</strong>—a testament to skill, hustle, and heart.</P>
                <P font="h2">But <strong>{completedData.trophies.firstPlace.length}</strong> teams took it all the way, dominating their courts and standing tall as <strong>First Place Champions!</strong></P>
                <P font="h2">Buckets were made. Ankles were "broken". <strong><i>Legends were born</i></strong>.</P>
            </Container>
        </Box>
    </>
);

const ClosedCard: React.FC<{ host: Host }> = ({ host }) => {
    return (
        <Box
            bgColor="tertiary"
            padding={{ phone: 'md', laptop: 'xxl' }}
            ms={{ phone: '0', laptop: '-3rem' }}
            width={{ phone: '100%', laptop: 'calc(100% + 3rem)' }}
            style={{ zIndex: 4 }}
            depth="lg"
        >
            <H1 font={{ phone: 'h1', laptop: 'jumbo2' }} uppercase mb="xxs">{host.activeTournament.title}</H1>
            <P
                mb="md"
                font={{ phone: 'leader', laptop: 'h1' }}
                uppercase
            >
                {dayjs(new Date(host.activeTournament.start)).format('MMMM Do')}
                {' - '}
                {dayjs(new Date(host.activeTournament.end)).format('Do')}
            </P>
            {host.activeTournament.location && (
                <Box>
                    <P weight="normal" mb="0">LOCATION</P>
                    <H3 mb="0">
                        {host.activeTournament.location.address}
                        <br/>
                        {host.activeTournament.location.city}, {host.activeTournament.location.state} {host.activeTournament.location.postcode}
                    </H3>
                </Box>
            )}
            <Grid mt="md" columns="repeat(auto-fit, 64px)" margin="auto">
                {host.facebook && (
                    <BtnDot tag="a" target="_blank" href={host.facebook} size="xl">
                        <FaFacebookF size="32px" />
                    </BtnDot>
                )}
                {host.instagram && (
                    <BtnDot tag="a" target="_blank" href={host.instagram} size="xl">
                        <FaInstagram size="32px" />
                    </BtnDot>
                )}
                {host.x && (
                    <BtnDot tag="a" target="_blank" href={host.x} size="xl">
                        <FaXTwitter size="32px" />
                    </BtnDot>
                )}
                {host.youtube && (
                    <BtnDot tag="a" target="_blank" href={host.x} size="xl">
                        <FaYoutube size="32px" />
                    </BtnDot>
                )}
                {host.tiktok && (
                    <BtnDot tag="a" target="_blank" href={host.tiktok} size="xl">
                        <FaTiktok size="32px" />
                    </BtnDot>
                )}
            </Grid>
        </Box>
    );
};

const HostHeader: React.FC<{host: Host}> = ({
    host,
}) => {
    const { scrollYProgress } = useScroll();
    const y = useParallax(scrollYProgress, 50);
    const { headerHeight } = useEnv();

    return (
        <Container size="stretch">
            <NoticeBar notices={host.notices} page={`host-${host.slug}`}/>
            <Grid
                className={styles.header}
                overflow="hidden"
                height="100%"
                minHeight={`calc(100vh - ${headerHeight}px)`}
            >
                <Box
                    className={styles.gallery}
                    overflow="hidden"
                    minHeight="480px"
                >
                    {host.gallery && host.gallery.length > 0 && (
                        <Gallery gallery={host.gallery} autoplay duration={5000} height="100%" parallax />
                    )}
                    {host.image && (!host.gallery || host.gallery.length === 0) && (
                        <motion.div key={host.image.id} style={{ y, scale: 1.3, width: '100%', height: '100%' }}>
                            <Image
                                image={host.image}
                                alt={host.name}
                                pb={{ phone: '240px', laptop: '60%' }}
                            />
                        </motion.div>
                    )}

                    {host.sponsors?.length > 0 && (
                        <div style={{
                            position: 'absolute',
                            width: '100%',
                            bottom: 0,
                            left: 0,
                            zIndex: 3,
                        }}>
                            <Type
                                bgColor="tertiary"
                                font="control"
                                bold
                                uppercase
                                display="block"
                                align="center"
                                padding={['xs', 'sm']}
                                width="min-content"
                                color="black"
                                lineHeight="1"
                                nowrap
                            >
                                Our Sponsors
                            </Type>
                                <Ticker duration={30}>
                                    {host.sponsors.map(sponsor => (
                                        <a key={sponsor.id} href={sponsor.url} target="_blank">
                                            <Box
                                                margin="sm"
                                                width="10vw"
                                                minWidth="100px"
                                                maxWidth="200px"
                                                minHeight="100px"
                                                maxHeight="200px"
                                                height="10vw"
                                                rounded="100%"
                                                depth="md"
                                                padding={sponsor.padding}
                                                bgColor={sponsor.bgColor || 'white'}
                                                overflow="hidden"
                                            >
                                                <Image
                                                    width="100%"
                                                    height="100%"
                                                    image={sponsor.logo}
                                                    alt={`${sponsor.name} logo`}
                                                    bgColor={sponsor.bgColor || 'white'}
                                                    objectFit="contain"
                                                />
                                            </Box>
                                        </a>
                                    ))}
                                </Ticker>
                            </div>
                        )}
                    </Box>
                <Box
                    width="100%"
                    bgColor="primary600"
                    verticalAlign="center"
                    bgImage="/images/b-ball-texture-2.svg"
                    style={{ zIndex: 4, backgroundAttachment: 'fixed' }}
                >   {host.activeTournament?.status === 'closed' && <ClosedCard host={host} />}
                    {host.activeTournament?.status !== 'closed' && (
                        <Box
                            bgColor="tertiary"
                            padding={{phone: 'md', laptop: 'xxl' }}
                            ms={{ phone: '0', laptop: '-3rem' }}
                            width={{ phone: '100%', laptop: 'calc(100% + 3rem)' }}
                            style={{ zIndex: 4 }}
                            depth="lg"
                        >
                            <H1 font={{ phone: 'h1', laptop: 'jumbo2' }} uppercase mb="xxs">{host.activeTournament.title}</H1>
                            <P
                                mb="xl"
                                font={{ phone: 'leader', laptop: 'h1' }}
                                uppercase
                            >
                                {dayjs(new Date(host.activeTournament.start)).format('MMMM Do')}
                                {' - '}
                                {dayjs(new Date(host.activeTournament.end)).format('Do')}
                            </P>
                            <Box mb="xl" width="min-content">
                                {host.activeTournament.status === 'open' && (
                                    <Link href={`/register/${host.activeTournament.slug}`}>
                                        <Btn
                                            size="xxl"
                                            font="h3"
                                            gradient={['secondary600', 'secondary']}
                                            color="secondary"
                                            bdWidth="6px"
                                            rounded="3rem"
                                            weight="bold"
                                            nowrap
                                            padding={{ phone: 'xs', laptop: ['xs', 'xxl', 'xs', 0] }}
                                            outline
                                            iconLeft={<Type color="primary" style={{ lineHeight: '0' }}><PiBasketball size="56px" /></Type>}
                                        >
                                            REGISTER NOW!
                                        </Btn>
                                    </Link>
                                )}
                                {host.activeTournament.status === 'active' && (
                                    <Btn
                                        size="xxl"
                                        font="h3"
                                        gradient={['secondary600', 'secondary']}
                                        color="secondary"
                                        bdWidth="6px"
                                        rounded="3rem"
                                        weight="bold"
                                        nowrap
                                        padding={{ phone: 'xs', laptop: ['xs', 'xxl', 'xs', 0] }}
                                        outline
                                        iconLeft={<Type color="primary" style={{ lineHeight: '0' }}><PiBasketball size="56px" /></Type>}
                                    >
                                        OPENING SOON!
                                    </Btn>
                                )}
                                <div
                                    style={{
                                        position: 'absolute',
                                        right: -80,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        pointerEvents: 'none',
                                    }}
                                    className="hide-on-mobile"
                                >
                                    {host.mackerMan && (<img src={host.mackerMan.url} alt="macker-man" width={140}/>)}
                                </div>
                            </Box>
                            <div>
                                <Type display="inline-block" font="leader">
                                    Team Entry Fee: <Type bold>${host.activeTournament.price}</Type>
                                </Type>
                                <br/>
                                {host.activeTournament.deadline && (
                                    <Type display="inline-block" font="leader">Deadline: <Type bold>{dayjs(host.activeTournament.deadline).format('MM/DD/YYYY')}</Type></Type>
                                )}
                                {!host.activeTournament.deadline && (
                                    <Type display="inline-block" font="leader">Deadline: <Type bold>{dayjs(host.activeTournament.start).format('MM/DD/YYYY')}</Type></Type>
                                )}
                            </div>
                            <Grid mt="md" columns="repeat(auto-fit, 64px)" margin="auto">
                                {host.facebook && (
                                    <BtnDot tag="a" target="_blank" href={host.facebook} size="xl">
                                        <FaFacebookF size="32px" />
                                    </BtnDot>
                                )}
                                {host.instagram && (
                                    <BtnDot tag="a" target="_blank" href={host.instagram} size="xl">
                                        <FaInstagram size="32px" />
                                    </BtnDot>
                                )}
                                {host.x && (
                                    <BtnDot tag="a" target="_blank" href={host.x} size="xl">
                                        <FaXTwitter size="32px" />
                                    </BtnDot>
                                )}
                                {host.youtube && (
                                    <BtnDot tag="a" target="_blank" href={host.x} size="xl">
                                        <FaYoutube size="32px" />
                                    </BtnDot>
                                )}
                                {host.tiktok && (
                                    <BtnDot tag="a" target="_blank" href={host.tiktok} size="xl">
                                        <FaTiktok size="32px" />
                                    </BtnDot>
                                )}
                            </Grid>
                        </Box>
                    )}
                </Box>
            </Grid>
        </Container>
    );
}

const Layout: React.FC<Props> = ({
    host,
    page = 'about',
    children,
    brackets,
    bracket,
}) => {
    const router = useRouter();

    const completedData = useMemo(() => {
        if (host.activeTournament?.status === 'finished') {
            const courts = bracket?.courts;

            const teams = courts?.map(court => {
                return court.teams;
            }).flat();

            const games = courts?.map(court => {
                return court.games;
            }).flat();

            const trophies = courts?.reduce((acc, court) => {
                acc.firstPlace.push(court.firstPlace);
                acc.secondPlace.push(court.secondPlace);
                acc.thirdPlace.push(court.thirdPlace);
                acc.tbPlace.push(court.tbPlace);
                acc.all += 4;
                return acc;
            }, {
                firstPlace: [],
                secondPlace: [],
                thirdPlace: [],
                tbPlace: [],
                all: 0,
            });
            return {
                courts,
                teams,
                games,
                trophies,
            }
        };
        return null;
    }, [host.activeTournament, bracket]);

    const tabs = useMemo(() => {
        const tabs = [{
            id: 'about',
            label: 'About',
        }] as {
            id: string,
            label: string,
        }[];

        if (bracket) {
            tabs.unshift({
                id: 'brackets',
                label: 'Brackets',
            });
        }

        if (host.activeTournament?.status === 'finished') {
            tabs.unshift({
                id: 'winners',
                label: 'Winners',
            });
        }

        if (host.mediaLayout?.length > 0) {
            tabs.push({
                id: 'media',
                label: 'Media',
            });
        }
        if (host.activeTournament?.schedule?.length > 0 || host.eventLayout?.length > 0) {
            tabs.push({
                id: 'schedule',
                label: 'Schedule',
            });
        }
        return tabs;
    }, [host.mediaLayout, host.activeTournament?.status, brackets]);

    const goToPath = (path) => {
        if (host.activeTournament?.status === 'finished') {
            if (path === 'winners') {
                router.push(`/local/${host.slug}`, null, { shallow: true, scroll: false });
            } else {
                router.push(`/local/${host.slug}/${path}`, null, { shallow: true, scroll: false });
            }
            return;
        } else if (brackets) {
            if (path === 'brackets') {
                router.push(`/local/${host.slug}`, null, { shallow: true, scroll: false });
            } else {
                router.push(`/local/${host.slug}/${path}`, null, { shallow: true, scroll: false });
            }
            return;
        } else if (path === 'about') {
            router.push(`/local/${host.slug}`, null, { shallow: true, scroll: false });
        } else {
            router.push(`/local/${host.slug}/${path}`, null, { shallow: true, scroll: false });
        }
    };

    return (
        <>
            <Box bgColor="neutral50">
                {completedData && <WinnersHeading host={host} completedData={completedData} />}
                {!completedData && <HostHeader host={host} />}
                    
                <Box>
                    <Container size="xl">
                        <Grid columns={{ phone: '1fr', laptop: `${host.sideLayout?.length && 'min-content '}3fr` }} columnGap="xxl">
                            <div>
                                {host.sideLayout?.length > 0 && (
                                    <Box width="360px" padding={{ phone: 'md', laptop: ['xl', 0, 0, 'md'] }} order={{ phone: 2, laptop: 0 }} className="hide-on-mobile">
                                        <Blocks blocks={host.sideLayout} />
                                    </Box>
                                )}
                            </div>
                            <Box bgColor="white" maxWidth="1024px" margin="auto">
                                <PageTabs
                                    tabs={tabs}
                                    selected={page}
                                    onChange={goToPath}
                                    label="Local Navigation"
                                />
                                {children}
                            </Box>
                        </Grid>
                    </Container>
                </Box>
            </Box>
        </>
    );
};

export default Layout;